import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack } from "@mui/system";

import { Modal } from "@/components";

import { useCreateCompaniesMutation } from "@/pages/Private/redux/companies/companies.api";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { InputField } from "@/components/InputField/InputField";
import { YearlyOrMonthly } from "@/enum/company.enum";

import { useGetProductsQuery } from "@/pages/Private/redux/subscription/subscription.api";

import { CreateCompanies, CreateCompaniesSchema } from "../schema/companies";

interface createCompaniesModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const CreateCompaniesModal: FunctionComponent<createCompaniesModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companies.${key}`);
	const [createCompanies, { isLoading, error }] = useCreateCompaniesMutation();

	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateCompanies>({
		defaultValues: {
			name: undefined,
			linkedinUrl: undefined,
			yearlyOrMonthly: undefined,
			demoCredits: 200,
			demoEndsInDays: 30,
			website: undefined,
		},
		resolver: zodResolver(CreateCompaniesSchema),
	});

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateCompanies>>({
		name: undefined,
		linkedinUrl: undefined,
		demoCredits: 200,
		demoEndsInDays: 30,
		website: undefined,
	});

	const { data: subscriptions, isLoading: isSubscriptionsLoading } = useGetProductsQuery();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof CreateCompanies) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const onSubmit = async (values: CreateCompanies) => {
		try {
			await createCompanies(values).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={ts("create.title")}
		>
			<Stack spacing={2}>
				<InputField
					error={!!errors.name?.message}
					handleChange={(e) => handleSaveFormState(e.target.value, "name")}
					label={ts("name")}
					name={"name"}
					placeholder={ts("name")}
					value={currentFormState.name ?? ""}
				/>

				<InputField
					error={!!errors.linkedinUrl?.message}
					handleChange={(e) => {
						handleSaveFormState(e.target.value, "linkedinUrl");
					}}
					label={ts("linkedinUrl")}
					name={"linkedinUrl"}
					placeholder={ts("linkedinUrl")}
					value={currentFormState.linkedinUrl || ""}
				/>

				<InputField
					error={!!errors.website?.message}
					handleChange={(e) => handleSaveFormState(e.target.value, "website")}
					label={"Website"}
					name={"website"}
					placeholder={"Website"}
					value={currentFormState.website || ""}
				/>

				<InputField
					error={!!errors.demoCredits?.message}
					handleChange={(e) => handleSaveFormState(+e.target.value, "demoCredits")}
					label={"Demo credits"}
					name={"demoCredits"}
					placeholder={"Demo credits"}
					value={currentFormState.demoCredits || "200"}
				/>

				<InputField
					error={!!errors.demoEndsInDays?.message}
					handleChange={(e) => handleSaveFormState(+e.target.value, "demoEndsInDays")}
					label={"Demo ends in days"}
					name={"demoEndsInDays"}
					placeholder={"Demo ends in days"}
					value={currentFormState.demoEndsInDays || "30"}
				/>

				<Dropdown
					data={[
						{
							title: "Monthly",
							id: YearlyOrMonthly.MONTHLY,
						},
						{
							title: "Yearly",
							id: YearlyOrMonthly.YEARLY,
						},
					]}
					defaultValue={
						currentFormState.subscription?.price.perMonthOrYear
							? {
									title:
										currentFormState.subscription?.price.perMonthOrYear === YearlyOrMonthly.MONTHLY
											? "Monthly"
											: "Yearly",
									id: currentFormState.subscription?.price.perMonthOrYear,
								}
							: undefined
					}
					floating={true}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id && value?.id !== currentFormState.yearlyOrMonthly) {
							handleSaveFormState(value?.id, "yearlyOrMonthly");
						}
					}}
					label="Type"
				/>

				<Dropdown
					data={
						subscriptions?.data.map(
							(subscription) =>
								({
									title: `${subscription.name} - ${
										subscription.prices.find(
											(price) => price.perMonthOrYear === currentFormState.yearlyOrMonthly
										)?.price
									}€ / ${subscription.prices
										.find((price) => price.perMonthOrYear === currentFormState.yearlyOrMonthly)
										?.perMonthOrYear?.toLowerCase()}`,
									id: subscription.prices.find(
										(price) => price.perMonthOrYear === currentFormState.yearlyOrMonthly
									)?.id,
								}) as AutoCompleteItem
						) || []
					}
					disabled={!currentFormState.yearlyOrMonthly || isSubscriptionsLoading}
					floating={true}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id && value?.id !== currentFormState.subscription?.id) {
							handleSaveFormState(value?.id, "subscriptionId");
						}
					}}
					label="Select another subscription"
				/>
			</Stack>
			{getAllErrors(error, formErrors).length ? (
				<Box sx={{ mt: 2 }}>{renderErrorMessages(getAllErrors(error, formErrors))}</Box>
			) : null}
		</Modal>
	);
};
